import { Link, useLocation } from 'react-router-dom'
import { useAccount } from '@/entities/session'
import { useCalls } from '@/entities/call'
import { nowInTimezone } from '@/shared/lib/time'
import styles from './Topbar.module.css'

interface TopbarProps {
  onSearch: () => void
  onMenu: () => void
}

/** Названия разделов для хлебных крошек. */
const TITLES: Record<string, string> = {
  '/': 'Обзор',
  '/test-call': 'Тестовый звонок',
  '/calls': 'Звонки',
  '/agenda': 'Что сделать',
  '/campaigns': 'Обзвоны',
  '/client-events': 'Что нового',
  '/onboarding': 'Настройка агента',
  '/settings': 'Настройка агента',
  '/settings/profile': 'Анкета',
  '/settings/voice': 'Голос',
  '/settings/hours': 'Часы звонков',
  '/settings/prompt': 'Промпт',
  '/settings/company': 'Компания',
}

export function Topbar({ onSearch, onMenu }: TopbarProps) {
  const { pathname } = useLocation()
  const { data: account } = useAccount()
  const calls = useCalls({})

  const crumbs = breadcrumbs(pathname)

  // Наговорено за всё время. Считается по завершённым звонкам: это то
  // число, за которое в конце концов выставляют счёт.
  const minutes = Math.round(
    (calls.data ?? []).reduce((sum, call) => sum + call.durationMs, 0) / 60000,
  )

  return (
    <header className={styles.topbar}>
      <button type="button" className={styles.menuButton} onClick={onMenu} aria-label="Меню">
        <MenuIcon />
      </button>

      <nav className={styles.crumbs} aria-label="Где я">
        {crumbs.map((crumb, i) => (
          <span key={crumb.path} className={styles.crumbs}>
            {i > 0 && <span className={styles.separator}>/</span>}
            {i === crumbs.length - 1 ? (
              <span className={styles.crumbCurrent}>{crumb.title}</span>
            ) : (
              <Link to={crumb.path} className={styles.crumbLink}>
                {crumb.title}
              </Link>
            )}
          </span>
        ))}
      </nav>

      <button type="button" className={styles.search} onClick={onSearch}>
        <SearchIcon />
        <span className={styles.searchLabel}>Поиск или переход…</span>
        <span className={styles.shortcut}>Ctrl K</span>
      </button>

      <div className={styles.right}>
        <span className={styles.usage} title="Наговорено за всё время">
          <ClockIcon />
          <span className={styles.usageValue}>{minutes}</span>
          мин
        </span>

        {account && (
          <span className={styles.usage} title="Время в часовом поясе компании">
            <span className={styles.usageValue}>{nowInTimezone(account.company.timezone)}</span>
          </span>
        )}

        <span className={styles.avatar} title={account?.user.email}>
          {initial(account?.user.name || account?.user.email)}
        </span>
      </div>
    </header>
  )
}

interface Crumb {
  path: string
  title: string
}

/**
 * Путь до текущей страницы.
 *
 * Карточка звонка — единственное место с двумя уровнями, поэтому
 * разбирается отдельным случаем, а не общим механизмом: механизм на
 * один случай сложнее самого случая.
 */
function breadcrumbs(pathname: string): Crumb[] {
  if (pathname.startsWith('/calls/')) {
    return [
      { path: '/calls', title: 'Звонки' },
      { path: pathname, title: 'Разговор' },
    ]
  }
  if (pathname.startsWith('/campaigns/')) {
    return [
      { path: '/campaigns', title: 'Обзвоны' },
      { path: pathname, title: 'Запуск' },
    ]
  }
  if (pathname.startsWith('/clients/')) {
    return [
      { path: '/campaigns', title: 'Обзвоны' },
      { path: pathname, title: 'Клиент' },
    ]
  }
  if (pathname.startsWith('/settings/')) {
    return [
      { path: '/settings', title: 'Настройка агента' },
      { path: pathname, title: TITLES[pathname] ?? 'Настройки' },
    ]
  }
  return [{ path: pathname, title: TITLES[pathname] ?? 'Кабинет' }]
}

function initial(value: string | undefined): string {
  return value?.trim().charAt(0) || '?'
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <circle cx="6.2" cy="6.2" r="4.4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9.4 9.4L12.2 12.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <circle cx="6" cy="6" r="4.6" stroke="currentColor" strokeWidth="1.3" />
      <path d="M6 3.6V6.2L7.6 7.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  )
}

function MenuIcon() {
  return (
    <svg width="16" height="12" viewBox="0 0 16 12" fill="none" aria-hidden="true">
      <path d="M1 1H15M1 6H15M1 11H15" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}
